(() => {
  const ANIMATION_MS = 340;
  const CHANGE_DEBOUNCE_MS = 300;
  const shopRoot = window.Shopify?.routes?.root || '/';
  const endpoint = (path) => `${shopRoot}${path}`;

  const state = {
    drawer: null,
    overlay: null,
    sectionRoot: null,
    full: null,
    items: null,
    empty: null,
    footer: null,
    status: null,
    toggles: [],
    background: [],
    sectionUrl: '',
    openOnAdd: true,
    closeTimeout: null,
    lastFocused: null,
    pendingChange: null,
    changeInFlight: false,
    labels: {}
  };

  const announce = (message) => {
    if (!state.status) return;
    state.status.textContent = '';
    window.requestAnimationFrame(() => {
      if (state.status) state.status.textContent = message || '';
    });
  };

  const setCount = (count) => {
    const safeCount = Math.max(0, Number(count) || 0);
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = String(safeCount);
      node.hidden = safeCount < 1;
    });
  };

  const refreshDrawerElements = () => {
    if (!state.drawer) return;
    state.full = state.drawer.querySelector('[data-cart-full]');
    state.items = state.drawer.querySelector('[data-cart-items]');
    state.empty = state.drawer.querySelector('[data-cart-empty]');
    state.footer = state.drawer.querySelector('[data-cart-footer]');
    state.status = state.drawer.querySelector('[data-cart-status]');
  };

  const refreshPageElements = () => {
    state.toggles = [...document.querySelectorAll('[data-cart-toggle]')];
    state.sectionRoot = state.drawer?.closest('.shopify-section') || state.drawer?.parentElement || null;
    state.background = [...document.body.children].filter((element) => {
      if (element === state.sectionRoot || element.contains(state.drawer)) return false;
      return !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName);
    });
  };

  const setBackgroundInert = (inert) => {
    state.background.forEach((element) => {
      element.inert = inert;
    });
  };

  const focusableElements = () => {
    if (!state.drawer) return [];
    return [...state.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
  };

  const openDrawer = (opener = document.activeElement) => {
    if (!state.drawer || !state.overlay) return;
    if (state.closeTimeout) window.clearTimeout(state.closeTimeout);

    refreshPageElements();
    state.lastFocused = opener instanceof HTMLElement ? opener : null;
    state.overlay.hidden = false;
    state.drawer.hidden = false;
    state.drawer.setAttribute('aria-hidden', 'false');
    setBackgroundInert(true);
    document.body.classList.add('cart-drawer-open');
    state.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));

    window.requestAnimationFrame(() => {
      if (!state.drawer || !state.overlay) return;
      state.overlay.classList.add('on');
      state.drawer.classList.add('on');
      const firstFocusable = focusableElements()[0];
      (firstFocusable || state.drawer).focus();
    });
  };

  const closeDrawer = () => {
    if (!state.drawer || !state.overlay || state.drawer.hidden) return;
    refreshPageElements();
    state.overlay.classList.remove('on');
    state.drawer.classList.remove('on');
    state.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    setBackgroundInert(false);
    state.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));

    if (state.lastFocused?.isConnected) state.lastFocused.focus();
    state.lastFocused = null;

    if (state.closeTimeout) window.clearTimeout(state.closeTimeout);
    state.closeTimeout = window.setTimeout(() => {
      if (!state.drawer?.classList.contains('on')) state.drawer.hidden = true;
      if (!state.overlay?.classList.contains('on')) state.overlay.hidden = true;
    }, ANIMATION_MS);
  };

  const applyEmptyState = (itemCount) => {
    if (!state.drawer || !state.full || !state.empty || !state.footer) return;
    const isEmpty = itemCount < 1;
    state.drawer.classList.toggle('empty', isEmpty);
    state.full.hidden = isEmpty;
    state.empty.hidden = !isEmpty;
    state.footer.hidden = isEmpty;
  };

  const renderBundledCartSection = (html, shouldAnnounce = true) => {
    if (!html || !state.drawer) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = parsed.querySelector('[data-cart-drawer]');
    if (!nextDrawer) return false;

    state.drawer.innerHTML = nextDrawer.innerHTML;
    refreshDrawerElements();

    const itemCount = Number(nextDrawer.dataset.cartItemCount);
    if (Number.isFinite(itemCount)) {
      state.drawer.dataset.cartItemCount = String(itemCount);
      setCount(itemCount);
      applyEmptyState(itemCount);
    }

    updateMutationState();
    if (shouldAnnounce) announce(state.labels.updated);
    return true;
  };

  const fetchCartSection = async () => {
    const url = new URL(state.sectionUrl || endpoint('cart'), window.location.origin);
    url.searchParams.set('section_id', 'cart-drawer');
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(state.labels.error || 'Cart section request failed');
    return response.text();
  };

  const refreshCart = async (shouldAnnounce = false) => {
    const html = await fetchCartSection();
    if (!renderBundledCartSection(html, shouldAnnounce)) {
      throw new Error(state.labels.error || 'Cart section request failed');
    }
  };

  const changeLine = async (key, quantity) => {
    const response = await fetch(endpoint('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: Math.max(0, quantity),
        sections: 'cart-drawer',
        sections_url: state.sectionUrl || window.location.pathname
      })
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.description || state.labels.error || 'Cart update failed');
    if (!data) throw new Error(state.labels.error || 'Cart update failed');
    return data;
  };

  const quantityControls = () => state.items
    ? [...state.items.querySelectorAll('[data-cart-qty-change]')]
    : [];

  const updateMutationState = () => {
    if (!state.drawer) return;
    const busy = Boolean(state.pendingChange || state.changeInFlight);
    state.drawer.setAttribute('aria-busy', String(busy));
    state.drawer.querySelectorAll('[data-cart-checkout]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = busy;
    });
    state.drawer.querySelectorAll('a[href]').forEach((link) => {
      if (busy) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    });
  };

  const restoreQuantityControls = (change) => {
    change.controls.forEach((control, index) => {
      if (control.isConnected) control.disabled = change.disabledStates[index];
    });
    if (change.valueNode?.isConnected) {
      change.valueNode.textContent = String(change.initialQuantity);
    }
  };

  const syncPendingQuantityControls = (change) => {
    change.controls.forEach((control) => {
      if (!change.item.contains(control)) {
        control.disabled = true;
        return;
      }

      if (control.dataset.cartAction === 'increase') {
        control.disabled = change.maximum !== null && change.quantity + change.increment > change.maximum;
      } else {
        control.disabled = false;
      }
    });

    if (change.valueNode?.isConnected) {
      change.valueNode.textContent = String(change.quantity);
    }
  };

  const cancelPendingChange = () => {
    const change = state.pendingChange;
    if (!change) return;
    if (change.timer) window.clearTimeout(change.timer);
    if (change.item.isConnected) change.item.classList.remove('is-loading');
    restoreQuantityControls(change);
    state.pendingChange = null;
    updateMutationState();
  };

  const commitPendingChange = async () => {
    const change = state.pendingChange;
    if (!change || state.changeInFlight) return;
    if (change.timer) window.clearTimeout(change.timer);

    state.pendingChange = null;
    state.changeInFlight = true;
    change.item.classList.add('is-loading');
    change.controls.forEach((control) => {
      control.disabled = true;
    });
    updateMutationState();
    let shouldRestoreFocus = false;

    try {
      const cart = await changeLine(change.key, change.quantity);
      const sectionRendered = renderBundledCartSection(cart.sections?.['cart-drawer']);
      if (!sectionRendered) {
        await refreshCart(true);
      }
      shouldRestoreFocus = true;
    } catch (error) {
      announce(error.message || state.labels.error);
      change.item.classList.remove('is-loading');
      restoreQuantityControls(change);
    } finally {
      state.changeInFlight = false;
      updateMutationState();

      if (shouldRestoreFocus && state.drawer?.classList.contains('on')) {
        const items = [...state.items.querySelectorAll('[data-cart-item]')];
        const targetItem = items[Math.min(change.itemIndex, Math.max(0, items.length - 1))];
        const target = targetItem?.querySelector(`[data-cart-action="${change.action}"]`);
        const fallback = state.drawer.querySelector('[data-cart-close]');
        if (target instanceof HTMLElement) target.focus();
        else if (fallback instanceof HTMLElement) fallback.focus();
      }
    }
  };

  const scheduleQuantityChange = (button, item) => {
    const key = item.dataset.cartKey;
    const action = button.dataset.cartAction;
    if (!key || !action || state.changeInFlight) return;

    let change = state.pendingChange;
    if (change && change.key !== key) return;

    if (!change) {
      const initialQuantity = Number(item.dataset.cartQuantity);
      const minimum = Math.max(1, Number(item.dataset.cartQuantityMin) || 1);
      const increment = Math.max(1, Number(item.dataset.cartQuantityIncrement) || 1);
      const maximumValue = Number(item.dataset.cartQuantityMax);
      const maximum = item.dataset.cartQuantityMax && Number.isFinite(maximumValue)
        ? maximumValue
        : null;
      const controls = quantityControls();

      if (!Number.isFinite(initialQuantity)) return;
      change = {
        key,
        item,
        itemIndex: [...state.items.querySelectorAll('[data-cart-item]')].indexOf(item),
        action,
        initialQuantity,
        quantity: initialQuantity,
        minimum,
        increment,
        maximum,
        controls,
        disabledStates: controls.map((control) => control.disabled),
        valueNode: item.querySelector('.cart-qty-value'),
        timer: null
      };
      state.pendingChange = change;
      announce(state.labels.updating);
    }

    change.action = action;

    if (action === 'increase') {
      const nextQuantity = change.quantity + change.increment;
      if (change.maximum === null || nextQuantity <= change.maximum) {
        change.quantity = nextQuantity;
      }
    } else if (action === 'decrease') {
      const nextQuantity = change.quantity - change.increment;
      change.quantity = nextQuantity < change.minimum ? 0 : nextQuantity;
    }

    if (change.quantity === change.initialQuantity) {
      cancelPendingChange();
      return;
    }

    change.item.classList.add('is-loading');
    syncPendingQuantityControls(change);
    updateMutationState();

    if (change.timer) window.clearTimeout(change.timer);
    if (change.quantity === 0) {
      commitPendingChange();
    } else {
      change.timer = window.setTimeout(commitPendingChange, CHANGE_DEBOUNCE_MS);
    }
  };

  const captureSubmitButton = (button) => {
    if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return null;
    const label = button instanceof HTMLButtonElement
      ? button.querySelector('[data-product-submit-label]')
      : null;
    const arrow = button instanceof HTMLButtonElement
      ? button.querySelector('[data-product-submit-arrow]')
      : null;

    return {
      text: button instanceof HTMLInputElement ? button.value : button.textContent || '',
      labelText: label?.textContent || '',
      arrowHidden: arrow instanceof HTMLElement ? arrow.hidden : null,
      disabled: button.disabled
    };
  };

  const setSubmitButtonText = (button, text, hideArrow = false) => {
    if (button instanceof HTMLInputElement) {
      button.value = text;
      return;
    }
    if (!(button instanceof HTMLButtonElement)) return;

    const label = button.querySelector('[data-product-submit-label]');
    const arrow = button.querySelector('[data-product-submit-arrow]');
    if (label instanceof HTMLElement) {
      label.textContent = text;
      if (hideArrow && arrow instanceof HTMLElement) arrow.hidden = true;
    } else {
      button.textContent = text;
    }
  };

  const restoreSubmitButton = (button, snapshot, form) => {
    if (!button?.isConnected) return;
    const label = button instanceof HTMLButtonElement
      ? button.querySelector('[data-product-submit-label]')
      : null;
    const arrow = button instanceof HTMLButtonElement
      ? button.querySelector('[data-product-submit-arrow]')
      : null;

    if (label instanceof HTMLElement) {
      label.textContent = snapshot?.labelText || '';
      if (arrow instanceof HTMLElement && snapshot?.arrowHidden !== null) {
        arrow.hidden = Boolean(snapshot?.arrowHidden);
      }
    } else {
      setSubmitButtonText(button, snapshot?.text || '');
    }
    button.removeAttribute('aria-busy');
    const selectedVariant = form.querySelector('[data-variant-radio]:checked, input[type="hidden"][data-variant-radio]');
    if (selectedVariant instanceof HTMLInputElement) {
      button.disabled = selectedVariant.dataset.available !== 'true';
    } else if (button instanceof HTMLButtonElement && button.matches('[data-quick-add-submit]')) {
      button.disabled = button.dataset.available !== 'true';
    } else {
      button.disabled = Boolean(snapshot?.disabled);
    }
  };

  const bindAddToCart = () => {
    const busyForms = new WeakSet();

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add') || !state.drawer) return;
      event.preventDefault();
      if (busyForms.has(form) || state.pendingChange || state.changeInFlight) {
        announce(state.labels.updating);
        return;
      }
      busyForms.add(form);

      const submitButton = event.submitter instanceof HTMLButtonElement || event.submitter instanceof HTMLInputElement
        ? event.submitter
        : form.querySelector('button[type="submit"], input[type="submit"]');
      const buttonSnapshot = captureSubmitButton(submitButton);
      let requestCompleted = false;

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        setSubmitButtonText(submitButton, state.labels.adding, true);
      }

      try {
        const formData = new FormData(form);
        formData.set('sections', 'cart-drawer');
        formData.set('sections_url', state.sectionUrl || `${window.location.pathname}${window.location.search}`);
        const response = await fetch(endpoint('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        requestCompleted = true;
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          announce(data?.description || state.labels.error);
          return;
        }

        const sectionRendered = renderBundledCartSection(data?.sections?.['cart-drawer']);
        if (!sectionRendered) await refreshCart(true);
        if (state.openOnAdd) openDrawer(submitButton);
      } catch (error) {
        if (!requestCompleted) {
          try {
            await refreshCart(false);
          } catch (cartError) {
          }
        }
        announce(state.labels.error);
      } finally {
        busyForms.delete(form);
        restoreSubmitButton(submitButton, buttonSnapshot, form);
      }
    }, { capture: true });
  };

  const onKeydown = (event) => {
    if (!state.drawer?.classList.contains('on')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = focusableElements();
    if (!focusable.length) {
      event.preventDefault();
      state.drawer.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const initDrawer = (scope = document) => {
    const drawer = scope instanceof Element && scope.matches('[data-cart-drawer]')
      ? scope
      : scope.querySelector?.('[data-cart-drawer]');
    const overlay = scope instanceof Element && scope.matches('[data-cart-overlay]')
      ? scope
      : scope.querySelector?.('[data-cart-overlay]');
    if (!(drawer instanceof HTMLElement) || !(overlay instanceof HTMLButtonElement)) return;

    state.drawer = drawer;
    state.overlay = overlay;
    refreshDrawerElements();
    refreshPageElements();
    state.sectionUrl = state.drawer.dataset.sectionUrl || '';
    state.openOnAdd = state.drawer.dataset.openOnAdd !== 'false';
    state.labels = {
      adding: state.drawer.dataset.addingText || '',
      updated: state.drawer.dataset.updatedText || '',
      updating: state.drawer.dataset.updatingText || '',
      error: state.drawer.dataset.errorText || ''
    };

    const itemCount = Number(state.drawer.dataset.cartItemCount);
    if (Number.isFinite(itemCount)) setCount(itemCount);
  };

  const resetDrawer = () => {
    if (state.closeTimeout) window.clearTimeout(state.closeTimeout);
    cancelPendingChange();
    setBackgroundInert(false);
    document.body.classList.remove('cart-drawer-open');
    refreshPageElements();
    state.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    state.drawer = null;
    state.overlay = null;
    state.sectionRoot = null;
    state.full = null;
    state.items = null;
    state.empty = null;
    state.footer = null;
    state.status = null;
    state.lastFocused = null;
    state.closeTimeout = null;
  };

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('[data-cart-toggle]');
    if (toggle instanceof HTMLElement) {
      event.preventDefault();
      openDrawer(toggle);
      try {
        await refreshCart(false);
      } catch (error) {
        announce(state.labels.error);
      }
      return;
    }

    if (!state.drawer || !target) return;

    const closeButton = target.closest('[data-cart-close]');
    if (closeButton && (state.drawer.contains(closeButton) || closeButton === state.overlay)) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const quantityButton = target.closest('[data-cart-qty-change]');
    if (quantityButton instanceof HTMLButtonElement && state.items?.contains(quantityButton)) {
      const item = quantityButton.closest('[data-cart-item]');
      if (item instanceof HTMLElement) scheduleQuantityChange(quantityButton, item);
      return;
    }

    if (state.pendingChange || state.changeInFlight) {
      const link = target.closest('a[href]');
      if (link && state.drawer.contains(link)) event.preventDefault();
    }
  });

  document.addEventListener('submit', (event) => {
    if (
      (state.pendingChange || state.changeInFlight)
      && event.target instanceof HTMLFormElement
      && event.target.matches('[data-cart-checkout-form]')
    ) {
      event.preventDefault();
    }
  });

  document.addEventListener('keydown', onKeydown);
  window.addEventListener('pageshow', async (event) => {
    if (!event.persisted || !state.drawer) return;
    try {
      await refreshCart(false);
    } catch (error) {
      announce(state.labels.error);
    }
  });

  document.addEventListener('shopify:section:load', (event) => initDrawer(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (state.drawer && event.target.contains(state.drawer)) resetDrawer();
  });

  bindAddToCart();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initDrawer(), { once: true });
  } else {
    initDrawer();
  }
})();
